import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useDropzone } from 'react-dropzone';
import { inpatientAPI } from '../../../../services/api'; // Adjust based on location
import { showSuccessToast, showErrorToast } from '../../../../utils/notificationUtils';
import './AttachmentsTab.css'; // Will create this next

const AttachmentsTab = ({ inpatientId, readOnly = false }) => {
    const { t } = useTranslation();
    const [attachments, setAttachments] = useState([]);
    const [loading, setLoading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);

    const getAttachmentSizeBytes = useCallback((att) => {
        if (!att) return null;
        const raw =
            att.file_size ??
            att.size ??
            att.filesize ??
            att.fileSize ??
            att.size_bytes ??
            att.size_in_bytes ??
            att.bytes ??
            null;

        if (raw === null || raw === undefined) return null;
        if (typeof raw === 'number') return Number.isFinite(raw) ? raw : null;

        const s = String(raw).trim();
        if (!s) return null;

        // Handle strings like "12.3 KB" or "1024 bytes"
        const m = s.match(/^([\d.]+)\s*(bytes|byte|b|kb|kib|mb|mib|gb|gib)$/i);
        if (m) {
            const n = parseFloat(m[1]);
            if (!Number.isFinite(n)) return null;
            const unit = m[2].toLowerCase();
            if (unit === 'bytes' || unit === 'byte' || unit === 'b') return n;
            if (unit === 'kb' || unit === 'kib') return n * 1024;
            if (unit === 'mb' || unit === 'mib') return n * 1024 * 1024;
            if (unit === 'gb' || unit === 'gib') return n * 1024 * 1024 * 1024;
        }

        // Fallback: try plain number string (assumed bytes)
        const n = parseFloat(s);
        return Number.isFinite(n) ? n : null;
    }, []);

    const formatFileSize = useCallback((bytes) => {
        if (bytes === 0) {
            return t('receptionOutpatients.attachments.fileSize.zero', { defaultValue: '0 Bytes' });
        }
        if (!Number.isFinite(bytes) || bytes === null || bytes === undefined) {
            return t('receptionOutpatients.attachments.metadata.unknown', { defaultValue: 'Unknown' });
        }

        const k = 1024;
        const units = ['bytes', 'kb', 'mb', 'gb'];
        const i = Math.min(units.length - 1, Math.floor(Math.log(bytes) / Math.log(k)));
        const size = parseFloat((bytes / Math.pow(k, i)).toFixed(2));
        return t('receptionOutpatients.attachments.fileSize.value', {
            defaultValue: '{{size}} {{unit}}',
            size,
            unit: t(`receptionOutpatients.attachments.fileSize.units.${units[i]}`, { defaultValue: units[i].toUpperCase() })
        });
    }, [t]);

    // Lightbox State
    const [lightboxOpen, setLightboxOpen] = useState(false);
    const [currentImage, setCurrentImage] = useState(null);

    const fetchAttachments = useCallback(async () => {
        if (!inpatientId) return;
        setLoading(true);
        try {
            // Check if backend implements getAttachments. If not, this will 404.
            // Assuming user will implement backend based on notification.
            const response = await inpatientAPI.getAttachments(inpatientId);
            if (response.data && Array.isArray(response.data.data)) {
                setAttachments(response.data.data);
            } else if (Array.isArray(response.data)) {
                setAttachments(response.data);
            }
        } catch (error) {
            console.error('Failed to load attachments', error);
            // showErrorToast(t('common.errorLoading')); 
            // Suppress error toast initially as backend might be missing
        } finally {
            setLoading(false);
        }
    }, [inpatientId]);

    useEffect(() => {
        fetchAttachments();
    }, [fetchAttachments]);

    const onDrop = useCallback(async (acceptedFiles) => {
        if (readOnly) return;

        const file = acceptedFiles[0];
        if (!file) return;

        setUploading(true);
        setUploadProgress(0);

        try {
            await inpatientAPI.uploadAttachment(inpatientId, file, (progressEvent) => {
                const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                setUploadProgress(percentCompleted);
            });
            showSuccessToast(t('common.uploadSuccess', { defaultValue: 'File uploaded successfully' }));
            fetchAttachments();
        } catch (error) {
            console.error('Upload failed', error);
            showErrorToast(t('common.uploadFailed', { defaultValue: 'Upload failed' }));
        } finally {
            setUploading(false);
            setUploadProgress(0);
        }
    }, [inpatientId, readOnly, t, fetchAttachments]);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        disabled: readOnly || uploading,
        multiple: false // One by one for now to show progress
    });

    const handleDelete = async (id) => {
        if (window.confirm(t('common.confirmDelete', { defaultValue: 'Are you sure you want to delete this file?' }))) {
            try {
                await inpatientAPI.deleteAttachment(inpatientId, id);
                showSuccessToast(t('common.deleteSuccess', { defaultValue: 'Deleted successfully' }));
                setAttachments(prev => prev.filter(a => a.id !== id));
            } catch (error) {
                console.error('Delete failed', error);
                showErrorToast(t('common.deleteFailed', { defaultValue: 'Delete failed' }));
            }
        }
    };

    const handlePreview = (attachment) => {
        const isImage = attachment.mime_type?.startsWith('image/') || attachment.file_name?.match(/\.(jpg|jpeg|png|gif|webp)$/i);
        if (isImage) {
            setCurrentImage(attachment);
            setLightboxOpen(true);
        } else {
            // Download non-image
            window.open(attachment.url, '_blank');
        }
    };

    const closeLightbox = () => {
        setLightboxOpen(false);
        setCurrentImage(null);
    };

    return (
        <div className="attachments-tab-container tab-content-inner">
            {/* Upload Area */}
            {!readOnly && (
                <div {...getRootProps()} className={`dropzone-area ${isDragActive ? 'active' : ''} ${uploading ? 'uploading' : ''}`}>
                    <input {...getInputProps()} />
                    {uploading ? (
                        <div className="upload-progress-state">
                            <div className="spinner-sm"></div>
                            <span>{t('receptionInpatients.details.attachments.uploading', { defaultValue: 'Uploading...' })} {uploadProgress}%</span>
                        </div>
                    ) : (
                        <div className="dropzone-content">
                            <i className="fas fa-cloud-upload-alt"></i>
                            <p>
                                {isDragActive
                                    ? t('receptionInpatients.details.attachments.dropHere', { defaultValue: 'Drop file here...' })
                                    : t('receptionInpatients.details.attachments.dropzone', { defaultValue: 'Drag & drop a file here, or click to select' })
                                }
                            </p>
                        </div>
                    )}
                </div>
            )}

            {/* List */}
            <div className="attachments-list">
                {loading ? (
                    <p className="loading-text">{t('common.loading', { defaultValue: 'Loading...' })}</p>
                ) : attachments.length === 0 ? (
                    <div className="empty-attachments">
                        <i className="fas fa-folder-open"></i>
                        <p>{t('receptionInpatients.details.attachments.empty', { defaultValue: 'No attachments added.' })}</p>
                    </div>
                ) : (
                    <div className="attachments-grid">
                        {attachments.map(att => {
                            const isImage = att.mime_type?.startsWith('image/') || att.file_name?.match(/\.(jpg|jpeg|png|gif|webp)$/i);
                            return (
                                <div key={att.id} className="attachment-card">
                                    <div className="preview-box" onClick={() => handlePreview(att)}>
                                        {isImage ? (
                                            <img src={att.url || att.original_url} alt={att.file_name} />
                                        ) : (
                                            <div className="file-icon-placeholder">
                                                <i className="fas fa-file-alt"></i>
                                                <span className="ext">{att.file_name?.split('.').pop()}</span>
                                            </div>
                                        )}
                                        <div className="hover-overlay">
                                            <i className={isImage ? "fas fa-search-plus" : "fas fa-download"}></i>
                                        </div>
                                    </div>
                                    <div className="attachment-info">
                                        <div className="filename" title={att.file_name}>{att.file_name}</div>
                                        <div className="meta">{formatFileSize(getAttachmentSizeBytes(att))}</div>
                                    </div>
                                    {!readOnly && (
                                        <button className="delete-att-btn" onClick={(e) => { e.stopPropagation(); handleDelete(att.id); }}>
                                            <i className="fas fa-times"></i>
                                        </button>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Lightbox */}
            {lightboxOpen && currentImage && (
                <div className="lightbox-overlay" onClick={closeLightbox}>
                    <button className="close-lightbox" onClick={closeLightbox}>&times;</button>
                    <div className="lightbox-content" onClick={e => e.stopPropagation()}>
                        <img
                            src={currentImage.url || currentImage.original_url}
                            alt={t('receptionInpatients.details.attachments.previewAlt', { defaultValue: 'Preview' })}
                        />
                        <div className="lightbox-footer">
                            <span>{currentImage.file_name}</span>
                            <a href={currentImage.url || currentImage.original_url} download target="_blank" rel="noopener noreferrer" className="lightbox-download">
                                <i className="fas fa-download"></i> {t('common.download', { defaultValue: 'Download' })}
                            </a>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AttachmentsTab;
